import { Capacitor } from '@capacitor/core'
import { PushNotifications } from '@capacitor/push-notifications'

import { Logger } from '../logger'
import { PushNotificationCallback, PushNotificationEvents, PushNotificationListeners } from './types'

const logger = new Logger('PushNotificationListener')

export class PushNotificationListener {
  private listeners: PushNotificationListeners
  private registrationToken: string | null = null

  constructor() {
    this.listeners = {
      onRegistration: [],
      onRegistrationError: [],
      onReceived: [],
      onActionPerformed: [],
    }

    this.setup()
  }

  public initialize(callbacks: PushNotificationListeners) {
    this.listeners = callbacks

    if (this.registrationToken) {
      this.notifyListeners('onRegistration', this.registrationToken)
    }
  }

  private setup() {
    const isNative = Capacitor.isNativePlatform()
    logger.info('Running on native device?', isNative)

    if (Capacitor.getPlatform() === 'web') return

    PushNotifications.addListener('registration', (token) => {
      logger.success('Token registered', token.value)
      this.registrationToken = token.value
      this.notifyListeners('onRegistration', token.value)
    })

    PushNotifications.addListener('registrationError', (error) => {
      logger.error('Registration error', error)
      this.notifyListeners('onRegistrationError', error)
    })

    PushNotifications.addListener('pushNotificationReceived', (notification) => {
      logger.info('Notification received', notification)
      this.notifyListeners('onReceived', notification)
    })

    PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
      logger.info('Notification action performed', notification)
      this.notifyListeners('onActionPerformed', notification)
    })
  }

  public on(event: PushNotificationEvents, callback: PushNotificationCallback) {
    if (this.listeners[event]) {
      this.listeners[event].push(callback)
    }
  }

  public off(event: PushNotificationEvents, callback: PushNotificationCallback) {
    if (this.listeners[event]) {
      this.listeners[event] = this.listeners[event].filter((cb) => cb !== callback)
    }
  }

  private notifyListeners(event: PushNotificationEvents, data: unknown) {
    if (this.listeners[event]) {
      this.listeners[event].forEach((callback) => callback(data))
    }
  }

  async removeAllListeners() {
    if (Capacitor.getPlatform() === 'web') return
    await PushNotifications.removeAllListeners()
  }
}
